/**
 * Resumable worksheet upload: mode selection (PDF or Scan), per-page upload, finalize to one PDF.
 * State machine: mode null | 'pdf' | 'scan', sessionId, pages[], pdfFile, status idle | uploading | processing | ready.
 * Mobile/iPad friendly; no gallery; camera only for scan; max 40 pages; uploads per page immediately.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  CameraAlt as ScanIcon,
  Cameraswitch as CameraswitchIcon,
  Close as CloseIcon,
  Refresh as RetryIcon,
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import {
  createOrResumeSession,
  getActiveSession,
  uploadPage,
  clearPages,
  uploadPdf,
  finalizeSession,
} from '../../services/worksheetUploadService';
import {
  WORKSHEET_MAX_PAGES,
} from '../../config/Config';
import { resolveImageDisplayUrl } from '../../config/Config';
import uploadService from '../../services/uploadService';

const FACING = { user: 'user', environment: 'environment' };

// ----- Helpers -----
function useActiveSession(params) {
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    const query = params && (params.test_attempt != null || params.test_id != null) ? params : {};
    getActiveSession(query)
      .then((res) => { if (!cancelled && res?.data) setActive(res.data); })
      .catch(() => { if (!cancelled) setActive(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params?.test_attempt, params?.test_id]);
  return { active, loading };
}

function useCameraStream(enabled, facing = FACING.environment) {
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  useEffect(() => {
    if (!enabled) {
      setStream(null);
      setError('');
      return;
    }
    let s = null;
    const startCamera = () => {
      return navigator.mediaDevices.getUserMedia({ video: { facingMode: facing }, audio: false });
    };
    startCamera()
      .then((st) => {
        s = st;
        setStream(st);
        setError('');
      })
      .catch((e) => setError(e.message || 'Camera access failed'));
    return () => {
      if (s) s.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
  }, [enabled, facing]);

  // Attach stream to video when both are available (video mounts when scan mode is shown)
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
    return () => {
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [stream]);
  return { videoRef, stream, error };
}

/**
 * Capture current video frame as JPEG Blob (camera only, no gallery).
 */
function captureFrameFromVideo(videoEl) {
  if (!videoEl || videoEl.videoWidth === 0) return null;
  const canvas = document.createElement('canvas');
  canvas.width = videoEl.videoWidth;
  canvas.height = videoEl.videoHeight;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(videoEl, 0, 0);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob || null), 'image/jpeg', 0.92);
  });
}

// ----- Main component -----
export default function ResumableWorksheetUpload({
  testAttemptId = null,
  testId = null,
  onComplete = null,
  onCancel = null,
  completedUpload = null,
  existingUpload = null,
  /** 'student' = StudentUpload ids; 'teacher' = TeacherUpload ids (roster worksheets). */
  worksheetUploadApiRole = 'student',
}) {
  const [mode, setMode] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [pages, setPages] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUploaded, setPdfUploaded] = useState(false);
  const [status, setStatus] = useState('idle');
  const [finalPdfUrl, setFinalPdfUrl] = useState(null);
  const [error, setError] = useState('');
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [switchModeDialogOpen, setSwitchModeDialogOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [cameraFacing, setCameraFacing] = useState(FACING.environment);
  const [handedOff, setHandedOff] = useState(false);
  const [pdfOpenLoading, setPdfOpenLoading] = useState(false);
  const lastCapturedBlobRef = useRef(null);

  const openStoredUploadPdf = async (uploadId, name) => {
    if (worksheetUploadApiRole === 'teacher') {
      await uploadService.openTeacherUploadPdf(uploadId, name);
    } else {
      await uploadService.openStudentUploadPdf(uploadId, name);
    }
  };

  const activeSessionParams = testId != null || testAttemptId != null
    ? { test_id: testId ?? undefined, test_attempt: testAttemptId ?? undefined }
    : null;
  const { active: activeSession, loading: activeLoading } = useActiveSession(activeSessionParams);
  const cameraEnabled = mode === 'scan' && !!sessionId;
  const { videoRef, stream, error: cameraError } = useCameraStream(cameraEnabled, cameraFacing);

  // Prompt resume when active session exists and user hasn't chosen mode yet
  useEffect(() => {
    if (activeLoading || mode !== null || !activeSession) return;
    setResumeDialogOpen(true);
  }, [activeLoading, activeSession, mode]);

  const startFresh = useCallback(async (selectedMode) => {
    setError('');
    try {
      const res = await createOrResumeSession({
        test_attempt: testAttemptId ?? undefined,
        test_id: testId ?? undefined,
        mode: selectedMode,
      });
      const data = res?.data;
      if (!data?.id) throw new Error('No session id');
      setSessionId(data.id);
      setMode(selectedMode);
      setPages(data.pages || []);
      setPdfFile(null);
      setPdfUploaded(false);
      setStatus('idle');
      setResumeDialogOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to create session');
    }
  }, [testAttemptId, testId]);

  const handleResume = useCallback(() => {
    if (!activeSession?.id) return;
    setSessionId(activeSession.id);
    setMode(activeSession.mode);
    setPages((activeSession.pages || []).map((p) => ({ ...p, uploadStatus: 'done' })));
    setPdfFile(null);
    setPdfUploaded(!!activeSession.final_pdf);
    setStatus('idle');
    setResumeDialogOpen(false);
  }, [activeSession]);

  const handleModeSelect = useCallback((selectedMode) => {
    if (mode !== null && mode !== selectedMode) {
      setPendingMode(selectedMode);
      setSwitchModeDialogOpen(true);
      return;
    }
    startFresh(selectedMode);
  }, [mode, startFresh]);

  const confirmSwitchMode = useCallback(async () => {
    if (sessionId && mode === 'scan') {
      try {
        await clearPages(sessionId);
      } catch (_) {}
    }
    setPages([]);
    setPdfFile(null);
    setPdfUploaded(false);
    setSwitchModeDialogOpen(false);
    setPendingMode(null);
    if (pendingMode) startFresh(pendingMode);
  }, [sessionId, mode, pendingMode, startFresh]);

  const handleCapture = useCallback(async () => {
    if (mode !== 'scan' || !sessionId || pages.length >= WORKSHEET_MAX_PAGES) return;
    const blob = await captureFrameFromVideo(videoRef.current);
    if (!blob) return;
    const file = new File([blob], `page_${Date.now()}.jpg`, { type: 'image/jpeg' });
    lastCapturedBlobRef.current = { file, pageNumber: pages.length + 1 };
    const pageEntry = {
      id: `pending-${Date.now()}`,
      page_number: pages.length + 1,
      image: null,
      uploaded_at: null,
      uploadStatus: 'uploading',
      error: null,
    };
    setPages((prev) => [...prev, pageEntry]);
    setError('');
    try {
      const res = await uploadPage(sessionId, file);
      const data = res?.data;
      setPages((prev) => prev.map((p) => (p.page_number === pageEntry.page_number
        ? { ...data || p, uploadStatus: 'done', error: null }
        : p)));
      lastCapturedBlobRef.current = null;
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Upload failed';
      setPages((prev) => prev.map((p) => (p.page_number === pageEntry.page_number
        ? { ...p, uploadStatus: 'error', error: msg }
        : p)));
    }
  }, [mode, sessionId, pages.length]);

  const handleRetryPage = useCallback(async (pageNumber) => {
    const entry = pages.find((p) => p.page_number === pageNumber);
    const cached = lastCapturedBlobRef.current;
    const file = cached?.pageNumber === pageNumber ? cached?.file : null;
    if (!file || !sessionId) return;
    setPages((prev) => prev.map((p) => (p.page_number === pageNumber ? { ...p, uploadStatus: 'uploading', error: null } : p)));
    try {
      const res = await uploadPage(sessionId, file);
      const data = res?.data;
      setPages((prev) => prev.map((p) => (p.page_number === pageNumber ? { ...data || p, uploadStatus: 'done', error: null } : p)));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Upload failed';
      setPages((prev) => prev.map((p) => (p.page_number === pageNumber ? { ...p, uploadStatus: 'error', error: msg } : p)));
    }
  }, [pages, sessionId]);

  const handlePdfSelect = useCallback((e) => {
    const file = e?.target?.files?.[0];
    if (!file || file.type !== 'application/pdf') return;
    setPdfFile(file);
    setError('');
  }, []);

  const handleUploadPdf = useCallback(async () => {
    if (!pdfFile || !sessionId) return;
    setStatus('uploading');
    setError('');
    try {
      await uploadPdf(sessionId, pdfFile);
      setPdfUploaded(true);
      setStatus('idle');
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Upload failed');
      setStatus('idle');
    }
  }, [pdfFile, sessionId]);

  /** PDF mode: upload to session then finalize and hand off to parent for OCI upload → main dialogue */
  const handleUploadAndFinalizePdf = useCallback(async () => {
    if (!pdfFile || !sessionId) return;
    setError('');
    setStatus('uploading');
    try {
      await uploadPdf(sessionId, pdfFile);
      setPdfUploaded(true);
      setStatus('processing');
      const res = await finalizeSession(sessionId);
      const url = res?.data?.final_pdf_url;
      setFinalPdfUrl(url || null);
      if (onComplete && url) {
        setHandedOff(true);
        onComplete(url);
      }
      setStatus('ready');
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Upload failed');
      setStatus('idle');
    }
  }, [pdfFile, sessionId, onComplete]);

  const handleFinalize = useCallback(async () => {
    if (!sessionId) return;
    const isScan = mode === 'scan';
    if (isScan && pages.length === 0) {
      setError('Add at least one page before finishing.');
      return;
    }
    if (mode === 'pdf' && !pdfUploaded) {
      setError('Upload the PDF first, then click Finish.');
      return;
    }
    setStatus('processing');
    setError('');
    try {
      const res = await finalizeSession(sessionId);
      const url = res?.data?.final_pdf_url;
      setFinalPdfUrl(url || null);
      if (onComplete && url) {
        setHandedOff(true);
        onComplete(url);
      }
      setStatus('ready');
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Finalization failed');
      setStatus('idle');
    }
  }, [sessionId, mode, pages.length, pdfUploaded, onComplete]);

  const canFinalize = mode === 'scan' ? pages.length > 0 : mode === 'pdf' && pdfUploaded;
  const uploadButtonEnabled = status === 'ready' && !!finalPdfUrl;

  // ----- Mode selection screen -----
  if (mode === null && !resumeDialogOpen) {
    return (
      <Box sx={{ p: 2, maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h6" gutterBottom>Worksheet upload</Typography>
        {existingUpload?.url && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Uploaded worksheet</Typography>
            <List dense>
              <ListItem
                component={existingUpload.id ? 'div' : 'a'}
                href={existingUpload.id ? undefined : existingUpload.url}
                target={existingUpload.id ? undefined : '_blank'}
                rel={existingUpload.id ? undefined : 'noopener noreferrer'}
                onClick={existingUpload.id ? async () => {
                  setPdfOpenLoading(true);
                  try {
                    await openStoredUploadPdf(existingUpload.id, existingUpload.filename || 'Worksheet.pdf');
                  } finally {
                    setPdfOpenLoading(false);
                  }
                } : undefined}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, cursor: existingUpload.id && !pdfOpenLoading ? 'pointer' : undefined, opacity: pdfOpenLoading ? 0.7 : 1 }}
                disabled={existingUpload.id ? pdfOpenLoading : undefined}
              >
                <ListItemAvatar>
                  <Box sx={{ width: 48, height: 48, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PdfIcon color="primary" />
                  </Box>
                </ListItemAvatar>
                <ListItemText
                  primary={existingUpload.filename || 'Worksheet.pdf'}
                  secondary={pdfOpenLoading ? 'Opening…' : 'Click to open or download'}
                />
              </ListItem>
            </List>
            {existingUpload.id ? (
              <Button variant="outlined" size="small" startIcon={pdfOpenLoading ? <CircularProgress size={16} /> : <UploadIcon />} onClick={async () => {
                setPdfOpenLoading(true);
                try {
                  await openStoredUploadPdf(existingUpload.id, existingUpload.filename || 'Worksheet.pdf');
                } finally {
                  setPdfOpenLoading(false);
                }
              }} disabled={pdfOpenLoading} sx={{ mt: 1 }}>
                {pdfOpenLoading ? 'Opening…' : 'Open / download PDF'}
              </Button>
            ) : (
              <Button variant="outlined" size="small" startIcon={<UploadIcon />} href={existingUpload.url} target="_blank" rel="noopener noreferrer" sx={{ mt: 1 }}>
                Open / download PDF
              </Button>
            )}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose how to add your worksheet: upload one PDF or scan pages with the camera.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card sx={{ cursor: 'pointer' }} onClick={() => handleModeSelect('pdf')}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PdfIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography fontWeight={600}>Upload PDF</Typography>
                <Typography variant="body2" color="text.secondary">Single PDF file</Typography>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ cursor: 'pointer' }} onClick={() => handleModeSelect('scan')}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ScanIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography fontWeight={600}>Scan with camera</Typography>
                <Typography variant="body2" color="text.secondary">Capture pages one by one (max {WORKSHEET_MAX_PAGES})</Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
        {onCancel && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>Cancel</Button>
          </Box>
        )}
      </Box>
    );
  }

  // ----- Ready: show new-dialogue success (uploaded worksheet from OCI) or "Adding…" until parent sets completedUpload -----
  if (status === 'ready') {
    if (handedOff && !completedUpload) {
      return (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography color="text.secondary">Adding to your uploads…</Typography>
        </Box>
      );
    }
    const hasUploadId = !!completedUpload?.id;
    const displayName = completedUpload?.filename || 'Worksheet.pdf';
    const displaySecondary = pdfOpenLoading ? 'Opening…' : (completedUpload
      ? 'Uploaded worksheet — click to open or download'
      : 'Generated worksheet — click to open or download');
    const openPdf = hasUploadId ? async () => {
      setPdfOpenLoading(true);
      try {
        await openStoredUploadPdf(completedUpload.id, displayName);
      } finally {
        setPdfOpenLoading(false);
      }
    } : undefined;
    const displayUrl = !hasUploadId && (completedUpload?.url || finalPdfUrl);
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="success" sx={{ mb: 2 }}>Worksheet ready.</Alert>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Uploaded worksheet</Typography>
        {(hasUploadId || displayUrl) && (
          <List dense sx={{ mb: 2 }}>
            <ListItem
              component={hasUploadId ? 'div' : 'a'}
              href={hasUploadId ? undefined : displayUrl}
              target={hasUploadId ? undefined : '_blank'}
              rel={hasUploadId ? undefined : 'noopener noreferrer'}
              onClick={openPdf}
              disabled={hasUploadId ? pdfOpenLoading : undefined}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, cursor: hasUploadId && !pdfOpenLoading ? 'pointer' : undefined, opacity: pdfOpenLoading ? 0.7 : 1 }}
            >
              <ListItemAvatar>
                <Box sx={{ width: 48, height: 48, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PdfIcon color="primary" />
                </Box>
              </ListItemAvatar>
              <ListItemText primary={displayName} secondary={displaySecondary} />
            </ListItem>
          </List>
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {hasUploadId && (
            <Button variant="contained" startIcon={pdfOpenLoading ? <CircularProgress size={20} /> : <UploadIcon />} onClick={openPdf} disabled={pdfOpenLoading}>
              {pdfOpenLoading ? 'Opening…' : 'Open / download PDF'}
            </Button>
          )}
          {displayUrl && !hasUploadId && (
            <Button variant="contained" startIcon={<UploadIcon />} href={displayUrl} target="_blank" rel="noopener noreferrer">
              Open / download PDF
            </Button>
          )}
          {onCancel && <Button onClick={onCancel}>Close</Button>}
        </Box>
      </Box>
    );
  }

  // ----- PDF mode -----
  if (mode === 'pdf') {
    const pdfBusy = status === 'uploading' || status === 'processing';
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Upload PDF</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <input accept="application/pdf" type="file" onChange={handlePdfSelect} style={{ display: 'none' }} id="ws-pdf-input" />
        <label htmlFor="ws-pdf-input">
          <Button variant="outlined" component="span" fullWidth startIcon={<PdfIcon />}>Choose PDF</Button>
        </label>
        {pdfFile && <Typography variant="body2" sx={{ mt: 1 }}>{pdfFile.name}</Typography>}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="contained" onClick={handleUploadAndFinalizePdf} disabled={!pdfFile || pdfBusy}>
            {pdfBusy ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
          <Button onClick={() => { setMode(null); setSessionId(null); setPages([]); setPdfFile(null); }}>Switch to scan</Button>
          {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        </Box>
      </Box>
    );
  }

  // ----- Scan mode -----
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>Scan pages (camera only, max {WORKSHEET_MAX_PAGES})</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {cameraError && <Alert severity="warning" sx={{ mb: 2 }}>{cameraError}</Alert>}
      <Paper variant="outlined" sx={{ p: 1, mb: 2, aspectRatio: '4/3', maxHeight: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
        {!stream && !cameraError && <CircularProgress />}
        <IconButton
          aria-label="Switch camera"
          title={cameraFacing === FACING.environment ? 'Switch to front camera' : 'Switch to back camera'}
          onClick={() => setCameraFacing((prev) => (prev === FACING.environment ? FACING.user : FACING.environment))}
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
          size="medium"
        >
          <CameraswitchIcon />
        </IconButton>
      </Paper>
      <Button
        variant="contained"
        fullWidth
        startIcon={<ScanIcon />}
        onClick={handleCapture}
        disabled={pages.length >= WORKSHEET_MAX_PAGES || !stream}
      >
        Capture page ({Math.min(pages.length + 1, WORKSHEET_MAX_PAGES)}/{WORKSHEET_MAX_PAGES})
      </Button>
      <List dense sx={{ mt: 2 }}>
        {pages.map((p) => (
          <ListItem key={p.id || p.page_number} alignItems="flex-start">
            <ListItemAvatar>
              {p.image ? (
                <img src={resolveImageDisplayUrl(p.image)} alt={`Page ${p.page_number}`} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }} />
              ) : (
                <Box sx={{ width: 48, height: 48, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {p.uploadStatus === 'uploading' && <CircularProgress size={24} />}
                  {p.uploadStatus === 'error' && <ErrorIcon color="error" />}
                  {p.uploadStatus === 'done' && <DoneIcon color="success" />}
                </Box>
              )}
            </ListItemAvatar>
            <ListItemText
              primary={`Page ${p.page_number}`}
              secondary={p.uploadStatus === 'error' ? p.error : (p.uploadStatus === 'uploading' ? 'Uploading…' : 'Uploaded')}
            />
            {p.uploadStatus === 'error' && (
              <IconButton size="small" onClick={() => handleRetryPage(p.page_number)} title="Retry">
                <RetryIcon />
              </IconButton>
            )}
          </ListItem>
        ))}
      </List>
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="contained" onClick={handleFinalize} disabled={!canFinalize || status === 'processing'}>
          {status === 'processing' ? <CircularProgress size={20} /> : 'Finish'}
        </Button>
        <Button onClick={() => { setPendingMode('pdf'); setSwitchModeDialogOpen(true); }}>Switch to PDF</Button>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
      </Box>

      {/* Resume dialog */}
      <Dialog open={resumeDialogOpen} onClose={() => setResumeDialogOpen(false)}>
        <DialogTitle>Unfinished worksheet</DialogTitle>
        <DialogContent>You have an active worksheet session. Resume or start over?</DialogContent>
        <DialogActions>
          <Button onClick={() => { setResumeDialogOpen(false); setMode(null); }}>Start over</Button>
          <Button variant="contained" onClick={handleResume}>Resume</Button>
        </DialogActions>
      </Dialog>
      {/* Switch mode confirmation */}
      <Dialog open={switchModeDialogOpen} onClose={() => setSwitchModeDialogOpen(false)}>
        <DialogTitle>Switch mode?</DialogTitle>
        <DialogContent>Scanned pages will be cleared. Continue?</DialogContent>
        <DialogActions>
          <Button onClick={() => { setSwitchModeDialogOpen(false); setPendingMode(null); }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirmSwitchMode}>Yes, switch</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
